import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Input, Select } from "antd";
import * as Yup from "yup";
import { AxiosResponse } from "axios";
import { ICustomersHoursFactory } from "../index";
import FormItem from "../../../../components/FormItem";
import filterOptions from "../../../../utils/filterOptions";
import { ICustomer } from "../../../Customers";
import api from "../../../../services/api";
import getValidationErrors from "../../../../utils/getValidationErrors";
import { removeNullKeys } from "../../../../utils/formatters";

interface AddHoursFactoryProps {
  selectHoursFactory: ICustomersHoursFactory | undefined;
  visible: boolean;
  handleClose: (hoursFactory?: ICustomersHoursFactory) => void;
  hoursFactory?: ICustomersHoursFactory[];
}

interface FormData {
  customer_id: string;
  value: number;
}

const layout = {
  labelCol: { span: 4 },
  wrapperCol: { span: 16 },
};

const AddHoursFactory: FC<AddHoursFactoryProps> = ({
  visible,
  selectHoursFactory,
  handleClose,
  hoursFactory,
}) => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [form] = Form.useForm<FormData>();

  useEffect(() => {
    if (selectHoursFactory) {
      form.setFieldsValue({
        value: selectHoursFactory.value,
        customer_id: selectHoursFactory.name,
      });
    } else {
      form.setFieldsValue({
        value: undefined,
        customer_id: undefined,
      });
    }
  }, [form, selectHoursFactory, visible]);

  useEffect(() => {
    if (visible) {
      setLoadingCustomers(true);
      api
        .get<ICustomer[]>("/customers")
        .then(response => {
          setCustomers(response.data);
        })
        .finally(() => {
          setLoadingCustomers(false);
        });
    }
  }, [visible]);

  const customerOptions = useMemo(() => {
    const hoursCustomerId = hoursFactory?.map(h => h?.customer_id);
    return customers
      .filter(customer => !customer?.hoursFactory)
      .map(customer => ({
        label: customer.name,
        value: customer.id,
      }))
      .filter(customer => !hoursCustomerId?.includes(customer.value));
  }, [customers, hoursFactory]);

  const handleSubmit = useCallback(
    async (data: FormData) => {
      setLoading(true);
      const clearData = removeNullKeys(data);
      try {
        const schema = Yup.object().shape({
          customer_id: Yup.string().required("Cliente é obrigatório"),
          value: Yup.number().required("Valor obrigatório"),
        });

        await schema.validate(clearData, { abortEarly: false });

        let response: AxiosResponse<ICustomersHoursFactory>;

        if (selectHoursFactory) {
          const payload = {
            value: data.value,
          };
          response = await api.patch(
            `/hours_factory/${selectHoursFactory.id}`,
            payload,
          );

        } else {
          const payload = {
            value: data.value,
            customer_id: data.customer_id,
          };
          response = await api.post("/hours_factory", payload);
        }
        handleClose(response.data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
        }
      } finally {
        setLoading(false);
      }
    },
    [selectHoursFactory],
  );

  const closeModal = useCallback(
    (hoursFactory?: ICustomersHoursFactory) => {
      handleClose(hoursFactory);
    },
    [form, handleClose],
  );

  return (
    <Modal
      title={selectHoursFactory ? "Editar" : "Adicionar"}
      style={{ width: 600 }}
      onCancel={() => closeModal()}
      visible={visible}
      onOk={() => form.submit()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
      destroyOnClose
    >
      <Form
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
        {...layout}
      >
        <FormItem
          name="customer_id"
          label="Cliente"
          required
          errors={validationErrors}
          initialValue=""
        >
          <Select
            showSearch
            allowClear
            filterOption={filterOptions}
            options={customerOptions}
            loading={loadingCustomers}
            disabled={!!selectHoursFactory}
          />
        </FormItem>
        <FormItem
          name="value"
          label="Valor"
          required
          errors={validationErrors}
          initialValue=""
        >
          <Input />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default AddHoursFactory;
